/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * Manuel arıza bildirimi: Traccar cevabı (sonucKodu / sonucMesaji) yalnızca bu POST yanıtındadır;
 * cihaz kaydındaki konum özeti alanlarına yazılmaz.
 */

import React, { useState, useMemo, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Text,
  Divider,
  Box,
  Badge,
  useToast,
} from "@chakra-ui/react";
import api from "utils/api";
import { useErrorHandler } from "hooks/useErrorHandler";
import { getErrorMessage } from "utils/errorHandler";

function formatUetdsDateTime(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function extractSoapFields(data) {
  if (!data || typeof data !== "object") {
    return { sonucKodu: "—", sonucMesaji: String(data ?? ""), payload: data };
  }
  const kod =
    data.sonucKodu ??
    data.sonuc_kodu ??
    data.resultCode ??
    data.code ??
    "—";
  const mes =
    data.sonucMesaji ??
    data.sonuc_mesaji ??
    data.resultMessage ??
    data.message ??
    "";
  return { sonucKodu: kod, sonucMesaji: mes, payload: data };
}

/** UETDS başarısı: konum bildirimi (uetds) kapatılır — inferTone ile aynı koşullar */
function isUetdsArizaSuccess(fieldRes) {
  const msg = String(fieldRes?.sonucMesaji ?? "").toLowerCase();
  const code = String(fieldRes?.sonucKodu ?? "").trim().toUpperCase();
  return (
    msg.includes("başarı") ||
    msg.includes("basari") ||
    code === "0" ||
    code === "OK"
  );
}

function inferTone(fieldRes) {
  if (isUetdsArizaSuccess(fieldRes)) return { scheme: "green", label: "Başarılı" };
  const msg = String(fieldRes?.sonucMesaji ?? "").toLowerCase();
  if (
    msg.includes("hata") ||
    msg.includes("başarısız") ||
    msg.includes("basarisiz") ||
    /\b(error|fail|failed)\b/.test(msg)
  )
    return { scheme: "red", label: "Hata / başarısız" };
  return { scheme: "blue", label: "SOAP yanıtı" };
}

export default function UetdsArizaModal({
  isOpen,
  onClose,
  arac,
  onSuccess,
}) {
  const { handleError } = useErrorHandler();
  const toast = useToast();
  const deviceId = arac?.id;
  const modalTitle = useMemo(() => {
    const p = arac?.plaka?.trim?.() || arac?.plaka;
    return p ? `Arıza bildir — ${p}` : "Manuel arıza bildirimi (UETDS)";
  }, [arac]);

  const [arizaBaşlangıç, setArizaBaşlangıç] = useState("");
  const [açıklama, setAçıklama] = useState("");
  const [submitting, setSubmitting] = useState(false);

  /** @type {[null | { sonucKodu: *, sonucMesaji: string, payload: object }, function]} */
  const [soapResult, setSoapResult] = useState(null);
  /** Başarılı arıza sonrası konum bildirimi kapatma: hiç denenmedi | tamam | hata */
  const [konumAutoOff, setKonumAutoOff] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setArizaBaşlangıç(formatUetdsDateTime(new Date()));
      setAçıklama("");
      setSubmitting(false);
      setSoapResult(null);
      setKonumAutoOff(null);
    }
  }, [isOpen, deviceId]);

  const handleSubmit = async () => {
    if (!deviceId) return;
    const trimmed = (açıklama || "").trim();
    const tarih = (arizaBaşlangıç || "").trim();
    if (!tarih || !trimmed) {
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/api/devices/${deviceId}/uetds/ariza`, {
        arizaBaslangicTarihi: tarih,
        aciklama: trimmed,
      });
      const data = await res.json();
      const fields = extractSoapFields(data);
      if (isUetdsArizaSuccess(fields)) {
        try {
          await api.put(`/api/devices/${deviceId}`, { uetds: false });
          setKonumAutoOff("ok");
        } catch (err) {
          handleError(err, "Konum bildirimi kapatma");
          setKonumAutoOff("fail");
        }
      } else {
        setKonumAutoOff(null);
      }
      setSoapResult(fields);
    } catch (e) {
      const userMsg = getErrorMessage(e, "Arıza bildirimi");
      toast({
        title: "Arıza bildirimi yapılamadı",
        description: userMsg,
        status: "error",
        duration: 12000,
        isClosable: true,
      });
      setSoapResult({
        sonucKodu: "—",
        sonucMesaji: userMsg,
        payload: e?.data ?? null,
      });
      setKonumAutoOff(null);
    } finally {
      setSubmitting(false);
    }
  };

  const finishAndClose = () => {
    if (soapResult && isUetdsArizaSuccess(soapResult)) {
      onSuccess?.(soapResult.payload ?? soapResult);
    }
    setSoapResult(null);
    setKonumAutoOff(null);
    onClose?.();
  };

  const closeWrap = () => {
    if (submitting) return;
    if (soapResult) finishAndClose();
    else onClose?.();
  };

  const tone = soapResult ? inferTone(soapResult) : null;

  return (
    <Modal isOpen={isOpen} onClose={closeWrap} isCentered size="md">
      <ModalOverlay />
      <ModalContent borderRadius="xl">
        <ModalHeader fontSize="md" pb={1}>
          {modalTitle}
        </ModalHeader>
        <ModalCloseButton isDisabled={submitting} />
        <ModalBody pb={soapResult ? 3 : undefined}>
          {!soapResult ? (
            <VStack align="stretch" spacing={3}>
              <Text fontSize="xs" color="gray.600">
                Tarih formatı: gün/ay/yıl ve 24 saat (örn. 08/05/2026 15:30). Plaka cihaz
                adından alınır. Yetki: yönetici (UETDS). Sonuç yalnızca bu istek yanıtındaki{" "}
                <Text as="span" fontWeight="600">
                  sonucKodu / sonucMesaji
                </Text>{" "}
                alanlarıyla gelir (cihaz kaydına yazılmaz).
              </Text>
              <FormControl>
                <FormLabel fontSize="sm">Arıza başlangıç zamanı</FormLabel>
                <Input
                  size="sm"
                  value={arizaBaşlangıç}
                  onChange={(e) => setArizaBaşlangıç(e.target.value)}
                  placeholder="dd/MM/yyyy HH:mm"
                  fontFamily="mono"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Açıklama</FormLabel>
                <Textarea
                  size="sm"
                  rows={4}
                  value={açıklama}
                  onChange={(e) => setAçıklama(e.target.value)}
                  placeholder="Arıza açıklaması (zorunlu)"
                />
              </FormControl>
            </VStack>
          ) : (
            <VStack align="stretch" spacing={3}>
              <HStack spacing={2} wrap="wrap" align="center">
                <Text fontSize="sm" fontWeight="600">
                  UETDS SOAP yanıtı
                </Text>
                <Badge colorScheme={tone.scheme} variant="solid" fontSize="0.7rem">
                  {tone.label}
                </Badge>
              </HStack>
              <Box>
                <Text fontSize="xs" color="gray.600">
                  Sonuç kodu
                </Text>
                <Text fontSize="md" fontWeight="600" fontFamily="mono" mt={0.5}>
                  {String(soapResult.sonucKodu)}
                </Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.600">
                  Sonuç mesajı
                </Text>
                <Box
                  mt={1}
                  px={3}
                  py={2}
                  borderRadius="md"
                  bg="gray.50"
                  borderWidth="1px"
                  borderColor="gray.200"
                  fontSize="sm"
                  wordBreak="break-word"
                  whiteSpace="pre-wrap"
                >
                  {(soapResult.sonucMesaji || "").trim() || "—"}
                </Box>
              </Box>
              <Divider />
              {konumAutoOff === "ok" ? (
                <Text fontSize="xs" color="green.700">
                  Periyodik konum bildirimi bu cihazda kapatıldı. Gerekirse menüden &quot;Konum
                  bildirimi ayarları&quot; ile yeniden manuel açabilirsiniz.
                </Text>
              ) : konumAutoOff === "fail" ? (
                <Text fontSize="xs" color="orange.700">
                  Arıza bildirimi UETDS tarafında başarılı görünüyor; ancak konum bildirimi
                  kapatılamadı. Lütfen &quot;Konum bildirimi ayarları&quot;ndan kapatarak doğrulayın.
                </Text>
              ) : null}
              <Text fontSize="xs" color="gray.500">
                Bu çıktı cihaz kartındaki “son konum bildirimi” alanlarına eklenmez; yalnızca bu
                bildirimin anlık SOAP sonucudur.
              </Text>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          {!soapResult ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => !submitting && onClose?.()} isDisabled={submitting}>
                İptal
              </Button>
              <Button
                colorScheme="blue"
                size="sm"
                onClick={handleSubmit}
                isLoading={submitting}
                isDisabled={!deviceId || !(açıklama || "").trim() || !(arizaBaşlangıç || "").trim()}
              >
                Gönder
              </Button>
            </>
          ) : (
            <Button colorScheme="blue" size="sm" onClick={finishAndClose}>
              Tamam
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
